import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { RotateCcw, ArrowLeft } from "lucide-react";
import { GameModes } from "../../config";
import {
  useAviatorLaunchMutation,
  useAviatrixLaunchMutation,
  useEuroVirtualLaunchUrlMutation,
  useFaziLaunchMutation,
  useGameLaunchUrlMutation,
  usePragmaticLaunchUrlMutation,
} from "../../store/apis";
import amole from "../../images/Icon.png";
import Layout from "../../layout";
import { useSelector } from "react-redux";
import {
  selectIsAuthenticated,
  selectUser,
  setLoginModal,
} from "../../store/auth-slice";
import { isMobileDevice } from "../../utils";
import { LogoSpinnerLoader } from "../../Components/Loaders";
import { toastService } from "../notificationPage/ToastContainer";
import BottomNavigation from "../../Components/BottonNavigation";

const GamePage = () => {
  const { gameAlias } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  const provider = queryParams.get("provider");
  const mode = queryParams.get("mode") || GameModes.DEMO;
  const isVirtual =
    provider === "eurovirtual" || location.pathname.includes("virtual-game");

  // Get user data once and store it in ref to avoid re-renders
  const user = useSelector(selectUser);
  const userRef = useRef(user);
  // Ref for iframe element to handle fullscreen
  const iframeRef = useRef(null);
  // Ref for the container that will go fullscreen
  const containerRef = useRef(null);

  // Update the ref when user changes but don't make component effects depend on user
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // State
  const [gameUrl, setGameUrl] = useState("");
  const [iframeError, setIframeError] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [gameInfo, setGameInfo] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sessionKey] = useState(() => `${gameAlias}-${provider}-${Date.now()}`);

  // API mutations
  const [generateGameUrl, { isLoading: isGettingGameLoading }] =
    useGameLaunchUrlMutation();
  const [generateVirtualGameUrl, { isLoading: isGettingVirtualGameLoading }] =
    useEuroVirtualLaunchUrlMutation();
  const [faziLaunch, { isLoading: isFaziLoading }] = useFaziLaunchMutation();
  const [aviatrixLaunch, { isLoading: isAviatrixLoading }] =
    useAviatrixLaunchMutation();
  const [pragmaticLaunch, { isLoading: pragmaticLoading }] =
    usePragmaticLaunchUrlMutation();
  const [aviator, { isLoading: aviatorLoading }] = useAviatorLaunchMutation();

  // Effect to update document title
  useEffect(() => {
    document.title = `${gameInfo?.name || gameAlias || "Game"} | Casino`;
  }, [gameInfo, gameAlias]);

  // Effect to log game URL for debugging
  useEffect(() => {
    console.log("Game URL:", gameUrl);
  }, [gameUrl]);

  // Check if user is logged in for real money mode
  useEffect(() => {
    if (mode === GameModes.REAL) {
      const currentUser = userRef.current;
      if (
        !currentUser ||
        (currentUser.balance != null && currentUser.balance < 0)
      ) {
        navigate("/casino");
        toastService.error("Error! Please login to proceed!!");
        setLoginModal(true);
      }
    }
  }, [mode, navigate]);

  useEffect(() => {
    if (mode === GameModes.REAL) {
      if (!isAuthenticated) {
        navigate("/casino");
        toastService.error("Error! Please login to proceed!!");
        setLoginModal(true);
      }
    }
  }, [isAuthenticated, mode, navigate, user]);

  // Fullscreen change detection
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isDocFullscreen =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      setIsFullscreen(!!isDocFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange
      );
    };
  }, []);

  // Handle fullscreen requests from the iframe
  useEffect(() => {
    // Handler for messages from the iframe
    const handleMessage = (event) => {
      // Accept messages from any origin since game providers might be on different domains
      if (
        event.data === "requestFullscreen" ||
        (typeof event.data === "object" &&
          event.data.type === "requestFullscreen")
      ) {
        console.log("Fullscreen request received from game");
        requestFullscreenForIframe();
      }
    };

    // Add event listener for messages
    window.addEventListener("message", handleMessage);

    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  // Function to handle fullscreen request
  const requestFullscreenForIframe = () => {
    // Use the container rather than the iframe directly
    const element = containerRef.current;
    if (!element) return;

    try {
      if (element.requestFullscreen) {
        element.requestFullscreen().catch((err) => {
          console.error(
            `Error attempting to enable fullscreen: ${err.message}`
          );
        });
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    } catch (error) {
      console.error("Error requesting fullscreen:", error);

      // Fallback: open in a new window if fullscreen fails
      if (gameUrl) {
        window.open(gameUrl, "_blank", "fullscreen=yes");
      }
    }
  };

  // Function to exit fullscreen
  const exitFullscreen = () => {
    try {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } catch (error) {
      console.error("Error exiting fullscreen:", error);
    }
  };

  // Memoize the game launch function to prevent recreation on each render
  const launchGame = useCallback(async () => {
    if (!gameAlias || !provider) return;

    const currentUser = userRef.current;

    if (mode === GameModes.REAL) {
      if (
        !currentUser ||
        (currentUser.balance != null && currentUser.balance < 1)
      ) {
        if (
          currentUser &&
          currentUser.balance != null &&
          currentUser.balance < 1
        ) {
          toastService.error("Error! Low balance, top up to proceed!!");
          navigate("/casino");
          return;
        }
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      let response;

      if (isVirtual) {
        response = await generateVirtualGameUrl({
          username: currentUser?.username,
          player_id: currentUser?.id,
          demo: mode,
        }).unwrap();
        setGameUrl(response.data.data.url);
      } else if (provider === "fazi") {
        response = await faziLaunch({
          username: currentUser?.username,
          customer_id: currentUser?.id,
          gameName: gameAlias,
          mobile: isMobileDevice(),
          mode: mode,
        }).unwrap();
        setGameUrl(response.url);
      } else if (provider === "aviatrix") {
        response = await aviatrixLaunch({
          customer_id: currentUser?.id,
          mode: mode,
        }).unwrap();
        setGameUrl(response.url);
      } else if (provider === "aviator") {
        response = await aviator({
          userId: currentUser?.id,
        }).unwrap();
        setGameUrl(response.url);
      } else if (provider === "pragmaticplay" || provider === "pragmaticlive") {
        response = await pragmaticLaunch({
          externalPlayerId: currentUser?.id,
          symbol: gameAlias,
          mode: mode,
        }).unwrap();
        setGameUrl(response.gameURL);
      } else {
        response = await generateGameUrl({
          username: currentUser?.username,
          customer_id: currentUser?.id,
          provider: provider,
          gameAlias: gameAlias,
          mobile: isMobileDevice(),
          mode: mode,
        }).unwrap();
        setGameUrl(response.response.url);
      }

      if (response.status === 200 && response.response?.gameInfo) {
        setGameInfo(response.response.gameInfo);
      }
    } catch (error) {
      console.error("Game launch error:", error);
      setError(
        error.data?.message || "Failed to launch game. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    gameAlias,
    provider,
    mode,
    isVirtual,
    navigate,
    generateGameUrl,
    generateVirtualGameUrl,
    faziLaunch,
    aviatrixLaunch,
    pragmaticLaunch,
    aviator,
  ]);

  // Launch game only once when component mounts
  useEffect(() => {
    launchGame();
  }, [launchGame]);

  const handleReload = () => {
    launchGame();
  };

  const handleReturnToGames = () => {
    navigate("/casino");
  };

  // Keyboard handler for fullscreen ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isFullscreen) {
        exitFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isFullscreen]);

  // Determine loading state by checking all possible loading states
  const isPageLoading =
    isLoading ||
    isGettingGameLoading ||
    isGettingVirtualGameLoading ||
    isFaziLoading ||
    isAviatrixLoading ||
    aviatorLoading ||
    pragmaticLoading;

  return (
    <Layout>
      <div className="flex-1 s15:pb-[100px]">
        <div className="h-full w-full md:px-5">
          {iframeError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 z-10">
              <div className="text-white text-center p-6 bg-gray-800 rounded-lg max-w-md">
                <p className="mb-4">Unable to load game directly.</p>
                <div className="flex gap-3 justify-center">
                  <a
                    href={gameUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    Open in new window
                  </a>
                  <button
                    onClick={handleReturnToGames}
                    className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                  >
                    Back to Games
                  </button>
                </div>
              </div>
            </div>
          )}

          {error ? (
            <div className="flex flex-col items-center justify-center w-full h-full min-h-[calc(100vh-200px)] text-red-500 p-4">
              <p className="text-center mb-3">{error}</p>
              <div className="flex gap-3">
                <button
                  onClick={handleReload}
                  className="px-4 py-2 bg-buttonGradient text-white rounded hover:bg-blue-600 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={handleReturnToGames}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Back to Games
                </button>
              </div>
            </div>
          ) : isPageLoading ? (
            <div className="flex items-center justify-center w-full h-full min-h-[calc(100vh-200px)]">
              <LogoSpinnerLoader logoSrc={amole} />
            </div>
          ) : (
            <div
              ref={containerRef}
              className={`relative w-full h-full min-h-[calc(100vh-200px)] ${
                isFullscreen ? "bg-black" : ""
              }`}
            >
              {isFullscreen && (
                <button
                  onClick={exitFullscreen}
                  className="absolute top-4 right-4 z-20 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-opacity"
                  aria-label="Exit fullscreen"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
                    <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
                    <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
                    <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
                  </svg>
                </button>
              )}
              <iframe
                ref={iframeRef}
                key={sessionKey}
                src={gameUrl}
                className="w-full h-full min-h-[calc(100vh-200px)] border-none"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen *; picture-in-picture"
                frameBorder="0"
                sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-storage-access-by-user-activation allow-top-navigation-by-user-activation allow-presentation"
                title={gameInfo?.name || gameAlias}
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                webkitAllowFullScreen="true"
                mozAllowFullScreen="true"
                msAllowFullScreen="true"
                onLoad={() => {
                  console.log(
                    `[Iframe Load] Successfully loaded game from URL: ${gameUrl}`
                  );
                  setIsLoading(false);

                  // Add fullscreen API to the iframe if supported
                  if (iframeRef.current && iframeRef.current.contentWindow) {
                    try {
                      // Try to communicate with the iframe
                      iframeRef.current.contentWindow.postMessage(
                        {
                          type: "fullscreenAPIAvailable",
                          containerFullscreen: true,
                        },
                        "*"
                      );
                    } catch (error) {
                      console.log(
                        "Cross-origin communication restricted. Fullscreen will work via message event listener."
                      );
                    }
                  }
                }}
                onError={(e) => {
                  console.error(
                    `[Iframe Error] Failed to load game from URL: ${gameUrl}`,
                    e
                  );
                  setIframeError(true);
                }}
                onAbort={(e) => {
                  console.error(
                    `[Iframe Abort] Game loading aborted for URL: ${gameUrl}`,
                    e
                  );
                }}
              />

              {/* {!isFullscreen && gameUrl && (
                <button
                  onClick={requestFullscreenForIframe}
                  className="absolute bottom-4 right-4 z-20 p-2 bg-blue-500 bg-opacity-80 rounded-full text-white hover:bg-opacity-100 transition-opacity"
                  aria-label="Enter fullscreen"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
                    <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
                    <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
                    <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
                  </svg>
                </button>
              )} */}
            </div>
          )}
        </div>
      </div>
      <BottomNavigation />
    </Layout>
  );
};

export default GamePage;
